import {
  adminBillingEventDetailSchema,
  adminBillingEventSchema,
  adminBillingReplayResultSchema,
  adminSubscriptionSchema,
  type AdminBillingEventDetail,
  type AdminBillingEventView,
  type AdminBillingReplayResult,
  type AdminSubscriptionView,
} from '@steado/admin-contract/billing';
import { paginationMetaSchema, type PaginationMeta } from '@steado/admin-contract/pagination';
import { queryOptions } from '@tanstack/react-query';
import { z } from 'zod';

import type { ApiClient, QueryValue } from '@/lib/api/client';

export const BILLING_PAGE_SIZE = 20;

export interface SubscriptionsFilters {
  readonly search?: string | undefined;
  readonly status?: string | undefined;
  readonly store?: string | undefined;
  readonly entitlement?: string | undefined;
  readonly cursor?: string | undefined;
}

export interface SubscriptionsPage {
  readonly items: AdminSubscriptionView[];
  readonly pagination: PaginationMeta;
}

const subscriptionsPageSchema = z.object({
  items: z.array(adminSubscriptionSchema),
  pagination: paginationMetaSchema,
});

export function subscriptionsQueryOptions(api: ApiClient, filters: SubscriptionsFilters) {
  const query: Record<string, QueryValue> = {
    search: filters.search,
    status: filters.status,
    store: filters.store,
    entitlement: filters.entitlement,
    cursor: filters.cursor,
    limit: BILLING_PAGE_SIZE,
  };

  return queryOptions({
    queryKey: ['admin', 'subscriptions', filters],
    queryFn: async (): Promise<SubscriptionsPage> => {
      const response = await api.requestWithMeta({
        path: '/admin/subscriptions',
        query,
        schema: z.array(adminSubscriptionSchema),
      });

      return subscriptionsPageSchema.parse({
        items: response.data,
        pagination: response.meta.pagination,
      });
    },
    placeholderData: (previous) => previous,
  });
}

export interface EventsFilters {
  readonly type?: string | undefined;
  readonly userId?: string | undefined;
  /** RevenueCat's own event id, matched as a substring. */
  readonly search?: string | undefined;
  readonly cursor?: string | undefined;
}

export interface EventsPage {
  readonly items: AdminBillingEventView[];
  readonly pagination: PaginationMeta;
}

const eventsPageSchema = z.object({
  items: z.array(adminBillingEventSchema),
  pagination: paginationMetaSchema,
});

export function billingEventsQueryOptions(api: ApiClient, filters: EventsFilters, enabled: boolean) {
  const query: Record<string, QueryValue> = {
    type: filters.type,
    userId: filters.userId,
    search: filters.search,
    cursor: filters.cursor,
    limit: BILLING_PAGE_SIZE,
  };

  return queryOptions({
    queryKey: ['admin', 'billing-events', filters],
    queryFn: async (): Promise<EventsPage> => {
      const response = await api.requestWithMeta({
        path: '/admin/billing/events',
        query,
        schema: z.array(adminBillingEventSchema),
      });

      return eventsPageSchema.parse({ items: response.data, pagination: response.meta.pagination });
    },
    enabled,
    placeholderData: (previous) => previous,
  });
}

export function billingEventQueryOptions(api: ApiClient, eventId: string | null) {
  return queryOptions({
    queryKey: ['admin', 'billing-event', eventId],
    queryFn: (): Promise<AdminBillingEventDetail> =>
      api.request({
        path: `/admin/billing/events/${eventId ?? ''}`,
        schema: adminBillingEventDetailSchema,
      }),
    enabled: eventId !== null,
  });
}

/**
 * Not a query. A replay re-applies an entitlement change and writes an audit
 * row, so it must happen exactly when an operator asks — never as a cache
 * refill or a background refetch.
 */
export function replayBillingEvent(api: ApiClient, eventId: string): Promise<AdminBillingReplayResult> {
  return api.request({
    path: `/admin/billing/events/${eventId}/replay`,
    method: 'POST',
    schema: adminBillingReplayResultSchema,
  });
}
